import { isDefined } from '@sgme/fp';
import { ISendMailSmartQuote } from 'apiConnector/life-ring';
import classNames from 'classnames';
import { useHelpCenterContact } from 'hooks/use-help-center-contact';
import { useAppSelector } from 'hooks/useAppSelector';
import { useToggleFeature } from 'hooks/useToggleFeature';
import LiferingModalWithScreenshot from 'layouts/main/sp-header/LiferingModalWithScreenshot';
import { ComponentProps, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { trackSmartQuoteEvent } from 'routes/smart-quote/utils';
import { QuoteResultsMap } from 'state/quotes/results/result.model';
import { SmartQuoteParsingResult } from 'state/workspaces/smart-quote/model';

type ShareFeedbackProps = {
  results: SmartQuoteParsingResult[];
};

function FeedbackButton({ className, ...props }: Omit<ComponentProps<'button'>, 'children'>) {
  return (
    <button className={classNames('btn btn-md btn-flat-secondary btn-icon-end', className)} {...props}>
      <FormattedMessage id="smartQuote.quickActions.shareFeedback" />
      <i className="icon icon-outlined" aria-hidden="true">
        feedback
      </i>
    </button>
  );
}

const getQuoteMailErrors = (quotesResult: QuoteResultsMap, results: SmartQuoteParsingResult[]): ISendMailSmartQuote[] =>
  results
    .filter(result => result.quoteStatus === 'Failed' || result.quoteStatus === 'Rejected' || result.status === 'Error')
    .map(result => {
      const quoteResult = quotesResult[result.rfqId];
      return {
        quoteId: result.rfqId,
        promptId: result.promptId,
        rejectionMessage:
          quoteResult?.rejections
            ?.map(r => r.rejectionMessage)
            .filter(isDefined)
            .join('; ') ?? result.error?.errorDescription,
        negotiationId: quoteResult?.negotiationId,
      };
    });

export function ShareFeedback({ results }: ShareFeedbackProps) {
  const quotesResult = useAppSelector(state => state.quotes.results);
  const hasFeature = useToggleFeature('helpCenterEnhancement');
  const errorQuotes = useMemo(() => getQuoteMailErrors(quotesResult, results), [results, quotesResult]);

  if (!errorQuotes?.length) {
    return null;
  }

  if (!hasFeature) {
    return <ShareFeedbackLifering smartQuotes={errorQuotes} />;
  }

  return <ShareFeedbackWithHelpCenter smartQuotes={errorQuotes} />;
}

type ShareFeedbackVariantProps = {
  smartQuotes: ISendMailSmartQuote[];
  className?: string;
};

function ShareFeedbackWithHelpCenter({ smartQuotes, className }: ShareFeedbackVariantProps) {
  const { isSuccess: isContactReady, openContactForm } = useHelpCenterContact();

  const handleClick = () => {
    const additionalMessage = smartQuotes.reduce(
      (acc, quote, index) => {
        const quoteNumber = index + 1;
        return {
          ...acc,
          [`${quoteNumber} - Quote ID`]: quote.quoteId,
          [`${quoteNumber} - Negotiation ID`]: quote.negotiationId ?? '',
          [`${quoteNumber} - Prompt ID`]: quote.promptId,
          [`${quoteNumber} - Rejection Message`]: quote.rejectionMessage ?? '',
        };
      },
      {} as Record<string, string | number>,
    );
    openContactForm(additionalMessage);
  };

  return <FeedbackButton className={className} onClick={handleClick} disabled={!isContactReady} />;
}

function ShareFeedbackLifering({ smartQuotes, className }: ShareFeedbackVariantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const openModal = () => {
    setIsOpen(true);
    trackSmartQuoteEvent('quote actions', 'share feedback');
  };

  return (
    <>
      <FeedbackButton className={className} onClick={openModal} />
      {isOpen && (
        <LiferingModalWithScreenshot onClose={() => setIsOpen(false)} isOpened={isOpen} smartQuotes={smartQuotes} />
      )}
    </>
  );
}
